import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles } from "@/mocks/melons";

type SearchOverlayProps = {
  open: boolean;
  onClose: () => void;
};

export default function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [keyword, setKeyword] = useState("");

  const results = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return articles.slice(0, 4);
    return articles
      .filter((item) =>
        item.title.toLowerCase().includes(key)
        || item.excerpt.toLowerCase().includes(key)
        || item.topicName.toLowerCase().includes(key)
        || item.tags.some((tag) => tag.toLowerCase().includes(key)),
      )
      .slice(0, 6);
  }, [keyword]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground-950/45 px-4 pt-20 md:pt-28">
      <button
        type="button"
        aria-label="关闭搜索"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-[640px] overflow-hidden rounded-lg border border-background-300 bg-background-50">
        <div className="flex items-center gap-3 border-b border-background-200 px-4 py-3">
          <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
            <i className="ri-search-line text-lg"></i>
          </span>
          <input
            autoFocus
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索事件、分类或标签，例如「时间线」"
            className="flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 cursor-pointer items-center justify-center text-foreground-500 transition-colors hover:text-foreground-900"
            aria-label="关闭"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-foreground-500">
              没有找到相关事件，换个关键词试试，或者直接去看
              <Link to="/topics" onClick={onClose} className="mx-1 text-primary-600 underline underline-offset-4">
                全部瓜田
              </Link>
            </p>
          ) : (
            <ul>
              {results.map((item) => (
                <li key={item.slug} className="border-b border-background-100 last:border-b-0">
                  <Link
                    to={`/article/${item.slug}`}
                    onClick={onClose}
                    className="flex flex-col gap-1 px-5 py-3 transition-colors hover:bg-background-100"
                  >
                    <span className="text-sm font-medium text-foreground-900">{item.title}</span>
                    <span className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span>{item.topicName}</span>
                      <span className="text-background-400">·</span>
                      <span>{item.date}</span>
                      <span className="text-background-400">·</span>
                      <span>热度 {item.heat}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}